import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const SettingsContext = createContext(null);

const STORAGE_KEY = "r6r:settings:v1";

/** Read persisted settings once at init. Returns null if nothing valid stored. */
const readStored = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Default values, used for anything missing from storage. */
const defaults = {
  mode: "single", // "single" | "team"
  rollMode: "cinematic", // "cinematic" | "instant" | "wheel"
  teamSize: 5,
  soundOn: true,
  excluded: [], // array on disk, Set in memory
  requiredRoles: { attacker: [], defender: [] }, // arrays on disk, Sets in memory
  loadoutToggles: { primary: false, secondary: false, gadget: false },
  selectedMapId: "none", // "none" | "random" | map id
  selectedSite: "random",
};

const merged = (() => {
  const stored = readStored();
  if (!stored || typeof stored !== "object") return defaults;
  return {
    ...defaults,
    ...stored,
    requiredRoles: {
      attacker: Array.isArray(stored?.requiredRoles?.attacker) ? stored.requiredRoles.attacker : [],
      defender: Array.isArray(stored?.requiredRoles?.defender) ? stored.requiredRoles.defender : [],
    },
    loadoutToggles: {
      ...defaults.loadoutToggles,
      ...(stored.loadoutToggles || {}),
    },
    excluded: Array.isArray(stored.excluded) ? stored.excluded : [],
  };
})();

export const SettingsProvider = ({ children }) => {
  const [mode, setMode] = useState(merged.mode);
  const [rollMode, setRollMode] = useState(merged.rollMode);
  const [teamSize, setTeamSize] = useState(merged.teamSize);
  const [soundOn, setSoundOn] = useState(merged.soundOn);
  const [excluded, setExcluded] = useState(() => new Set(merged.excluded));
  const [requiredRoles, setRequiredRoles] = useState({
    attacker: new Set(merged.requiredRoles.attacker),
    defender: new Set(merged.requiredRoles.defender),
  });
  const [loadoutToggles, setLoadoutToggles] = useState(merged.loadoutToggles);
  const [selectedMapId, setSelectedMapId] = useState(merged.selectedMapId);
  const [selectedSite, setSelectedSite] = useState(merged.selectedSite);

  // Persist whenever anything changes. Sets get converted to arrays for JSON.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    try {
      const payload = {
        mode,
        rollMode,
        teamSize,
        soundOn,
        excluded: Array.from(excluded),
        requiredRoles: {
          attacker: Array.from(requiredRoles.attacker),
          defender: Array.from(requiredRoles.defender),
        },
        loadoutToggles,
        selectedMapId,
        selectedSite,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* quota / disabled storage — silently ignore */
    }
  }, [
    mode,
    rollMode,
    teamSize,
    soundOn,
    excluded,
    requiredRoles,
    loadoutToggles,
    selectedMapId,
    selectedSite,
  ]);

  const toggleExcluded = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearExcluded = () => setExcluded(new Set());

  const toggleRequiredRole = (sideKey, roleId) => {
    setRequiredRoles((prev) => {
      const nextSet = new Set(prev[sideKey]);
      if (nextSet.has(roleId)) nextSet.delete(roleId);
      else nextSet.add(roleId);
      return { ...prev, [sideKey]: nextSet };
    });
  };

  const toggleLoadout = (key) =>
    setLoadoutToggles((prev) => ({ ...prev, [key]: !prev[key] }));

  const value = {
    mode,
    setMode,
    rollMode,
    setRollMode,
    teamSize,
    setTeamSize,
    soundOn,
    setSoundOn,
    excluded,
    toggleExcluded,
    clearExcluded,
    requiredRoles,
    toggleRequiredRole,
    loadoutToggles,
    toggleLoadout,
    selectedMapId,
    setSelectedMapId,
    selectedSite,
    setSelectedSite,
  };

  return (
    <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside <SettingsProvider>");
  return ctx;
};
